import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "fixed inset-y-0 left-0 z-50 w-72 bg-sidebar border-r border-sidebar-border shadow-xl md:hidden overflow-y-auto"
}
const _hoisted_2 = { class: "flex items-center gap-3 p-4 border-b border-sidebar-border" }
const _hoisted_3 = { class: "flex flex-col gap-0.5 leading-none" }
const _hoisted_4 = { class: "text-xs text-muted-foreground" }
const _hoisted_5 = { class: "p-2" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "mt-3 mb-1 px-3" }
const _hoisted_8 = { class: "text-xs font-semibold uppercase tracking-wider text-muted-foreground" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "absolute bottom-0 left-0 right-0 border-t border-sidebar-border p-3 bg-sidebar" }
const _hoisted_11 = { class: "flex items-center gap-3" }
const _hoisted_12 = { class: "flex-1 min-w-0" }
const _hoisted_13 = { class: "text-sm font-medium text-sidebar-foreground truncate" }
const _hoisted_14 = { class: "text-xs text-muted-foreground truncate" }
const _hoisted_15 = { class: "flex items-center justify-center h-14 shrink-0" }
const _hoisted_16 = { class: "flex-1 flex flex-col items-center gap-1 px-2 pt-2 overflow-y-auto scrollbar-none" }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = { class: "text-[10px] leading-tight mt-1.5 w-full text-center truncate" }
const _hoisted_19 = { class: "flex flex-col items-center py-4 border-t border-sidebar-border shrink-0" }
const _hoisted_20 = { class: "flex items-center justify-between h-14 px-4 border-b border-sidebar-border shrink-0" }
const _hoisted_21 = { class: "text-sm font-semibold text-sidebar-foreground" }
const _hoisted_22 = { class: "flex-1 overflow-y-auto p-2" }
const _hoisted_23 = {
  key: 0,
  class: "my-1.5 mx-2 border-t border-sidebar-border"
}
const _hoisted_24 = {
  key: 1,
  class: "px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
}
const _hoisted_25 = ["onClick"]
const _hoisted_26 = ["onClick"]
const _hoisted_27 = {
  key: 0,
  class: "px-4 py-3 border-t border-sidebar-border"
}
const _hoisted_28 = { class: "text-xs text-muted-foreground truncate" }

import { ref, computed, onMounted, onUnmounted, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useCopilotWidget } from '@/composables/useCopilotWidget'
import { getInitials } from '@/utils/formatters'
import {
  LayoutDashboard,
  Settings,
  Users,
  Building2,
  FolderKanban,
  FileText,
  Ticket,
  Calendar,
  TrendingUp,
  DollarSign,
  Rocket,
  Flame,
  MessageSquare,
  MessageSquarePlus,
  History,
  LogOut,
  BotMessageSquare,
  KanbanSquare,
  AppWindow,
  Boxes,
  Shield,
  UserPlus,
  Terminal,
  Network,
  Code2,
  GitBranch,
  PenLine,
  Plug,
  CheckSquare,
  MessageSquareHeart,
  ClipboardList,
  SquareTerminal,
  Menu,
  X,
} from 'lucide-vue-next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

interface MenuItem {
  title: string
  url: string
  icon: Component
  roles?: string[]
}

interface MenuGroup {
  label?: string
  items: MenuItem[]
}

interface ModuleSection {
  id: string
  label: string
  icon: Component
  items: MenuItem[]
  groups?: MenuGroup[]
  directUrl?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppSidebar',
  setup(__props) {

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const {
  user,
  logout,
  currentTenant,
  isSuperAdmin,
  isAdmin,
  isOperator,
  isClient,
  isMember,
  isModuleEnabled,
} = useAuth()

const { open: openCopilot } = useCopilotWidget()

const activeModule = ref<string | null>(null)
const isMobileMenuOpen = ref(false)
const drawerRef = ref<HTMLElement | null>(null)
const railRef = ref<HTMLElement | null>(null)

// Navigation items with role restrictions
const commercialItems = computed<MenuItem[]>(() => [
  { title: t('navigation.pipeline'), url: '/crm/pipeline', icon: TrendingUp, roles: ['admin'] },
  { title: t('navigation.prospects'), url: '/crm/prospects', icon: Users, roles: ['admin'] },
  { title: t('navigation.quotations'), url: '/quotations', icon: MessageSquare, roles: ['admin', 'client', 'member'] },
  { title: t('navigation.salesOrders'), url: '/sales-orders', icon: DollarSign, roles: ['admin'] },
  { title: 'Ignite', url: '/ignite', icon: Flame, roles: ['superadmin'] },
])

// Project items split into groups for the drawer
const projectGeneralItems = computed<MenuItem[]>(() => [
  { title: t('navigation.organizations'), url: '/organizations', icon: Building2, roles: ['admin', 'operator'] },
  { title: t('navigation.myOrganization'), url: '/my-organization', icon: Building2, roles: ['client'] },
  { title: t('navigation.projects'), url: '/projects', icon: FolderKanban },
  { title: 'Entregables', url: '/deliverables', icon: Boxes },
])

const projectDevItems = computed<MenuItem[]>(() => [
  { title: t('navigation.applications'), url: '/applications', icon: AppWindow },
  { title: t('navigation.requirements'), url: '/requirements', icon: FileText },
  { title: t('navigation.releases'), url: '/releases', icon: Rocket, roles: ['admin', 'operator'] },
  { title: t('navigation.backlog'), url: '/backlog', icon: KanbanSquare, roles: ['admin', 'operator'] },
  { title: t('navigation.implementations'), url: '/implementations', icon: Rocket, roles: ['superadmin', 'admin', 'operator'] },
])

// Flat list for module visibility check
const projectItems = computed<MenuItem[]>(() => [
  ...projectGeneralItems.value,
  ...projectDevItems.value,
])

const supportItems = computed<MenuItem[]>(() => [
  { title: t('navigation.petitions'), url: '/petitions', icon: MessageSquarePlus },
  { title: t('navigation.tickets'), url: '/tickets', icon: Ticket },
  { title: 'Calendario', url: '/calendar', icon: Calendar },
])

const adminItems = computed<MenuItem[]>(() => [
  { title: t('navigation.users'), url: '/users', icon: Users, roles: ['admin'] },
  { title: t('navigation.activityLog'), url: '/activity-log', icon: History, roles: ['superadmin'] },
  { title: t('navigation.settings'), url: '/settings', icon: Settings, roles: ['admin'] },
])

// Dev Center — herramientas internas de desarrollo (espeja el grupo del mobile).
// Gateado por el módulo `devCenter` del tenant + rol admin; el superadmin ve todo.
const devCenterItems = computed<MenuItem[]>(() => [
  { title: 'Grafo de conocimiento', url: '/dev-center/knowledge-graph', icon: Network },
  { title: 'Grafo de código', url: '/dev-center/codegraph', icon: Code2 },
  // TPL-94: dos entradas separadas (paridad con el drawer del mobile) — terminal V1 (xterm) vs
  // sesiones de agente V2 (chat continuable). Cada una es su propia vista/lista.
  { title: 'Sesiones de terminal', url: '/dev-center/sessions', icon: Terminal },
  { title: 'Sesiones de agente', url: '/dev-center/agent-sessions', icon: BotMessageSquare },
  { title: 'Pipelines', url: '/dev-center/pipelines', icon: GitBranch },
])

const superAdminItems = computed<MenuItem[]>(() => [
  { title: 'Dashboard', url: '/super-admin', icon: Shield, roles: ['superadmin'] },
  { title: 'Tenants', url: '/super-admin/tenants', icon: Building2, roles: ['superadmin'] },
  { title: 'Leads', url: '/leads', icon: UserPlus, roles: ['superadmin'] },
  // Sessions y Grafo de conocimiento se movieron al grupo "Dev Center" (arriba).
  { title: 'Blog', url: '/super-admin/blog', icon: PenLine, roles: ['superadmin'] },
  { title: 'Feedback', url: '/super-admin/feedback', icon: MessageSquareHeart, roles: ['superadmin'] },
])

const filterByRole = (items: MenuItem[]): MenuItem[] => {
  const userRole = user.value?.role
  if (!userRole) return []
  return items.filter((item) => {
    if (!item.roles) return true
    return item.roles.includes(userRole)
  })
}

const isCommercialEnabled = computed(() => isModuleEnabled.value('commercial'))

const modules = computed<ModuleSection[]>(() => {
  const sections: ModuleSection[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      items: [],
      directUrl: '/dashboard',
    },
  ]

  // Copilot as direct action
  if (isSuperAdmin.value || ((isAdmin.value || isOperator.value) && isModuleEnabled.value('agent'))) {
    sections.push({
      id: 'copilot',
      label: 'Copilot',
      icon: BotMessageSquare,
      items: [],
    })
  }

  // Dev Center: justo debajo de Copilot. Visible para el superadmin (ve todo) o para
  // admins con el módulo `devCenter` activo en su tenant.
  if (isSuperAdmin.value || (isAdmin.value && isModuleEnabled.value('devCenter'))) {
    sections.push({
      id: 'devcenter',
      label: 'Dev Center',
      icon: SquareTerminal,
      items: devCenterItems.value,
    })
  }

  const filteredCommercial = filterByRole(commercialItems.value)
  if (isCommercialEnabled.value && filteredCommercial.length > 0) {
    sections.push({
      id: 'commercial',
      label: t('navigation.commercial'),
      icon: TrendingUp,
      items: filteredCommercial,
    })
  }

  const filteredProjectGeneral = filterByRole(projectGeneralItems.value)
  const filteredProjectDev = filterByRole(projectDevItems.value)
  const allProjectItems = [...filteredProjectGeneral, ...filteredProjectDev]
  if (allProjectItems.length > 0) {
    const projectGroups: MenuGroup[] = []
    if (filteredProjectGeneral.length > 0) {
      projectGroups.push({ items: filteredProjectGeneral })
    }
    if (filteredProjectDev.length > 0) {
      projectGroups.push({ label: 'Software', items: filteredProjectDev })
    }
    sections.push({
      id: 'projects',
      label: t('navigation.projects'),
      icon: FolderKanban,
      items: allProjectItems,
      groups: projectGroups,
    })
  }

  // Tareas as direct link (admin/operator only)
  if (isAdmin.value || isOperator.value) {
    sections.push({
      id: 'tasks',
      label: 'Tareas',
      icon: CheckSquare,
      items: [],
      directUrl: '/tasks',
    })
  }

  const filteredSupport = filterByRole(supportItems.value)
  if (filteredSupport.length > 0) {
    sections.push({
      id: 'management',
      label: t('navigation.management'),
      icon: ClipboardList,
      items: filteredSupport,
    })
  }

  // Integraciones as direct link
  if (isAdmin.value || isOperator.value) {
    sections.push({
      id: 'integrations',
      label: 'Integraciones',
      icon: Plug,
      items: [],
      directUrl: '/integrations',
    })
  }

  const filteredAdmin = filterByRole(adminItems.value)
  if (filteredAdmin.length > 0) {
    sections.push({
      id: 'admin',
      label: t('navigation.settings'),
      icon: Settings,
      items: filteredAdmin,
    })
  }

  const filteredSuperAdmin = filterByRole(superAdminItems.value)
  if (filteredSuperAdmin.length > 0) {
    sections.push({
      id: 'superadmin',
      label: 'SuperAdmin',
      icon: Shield,
      items: filteredSuperAdmin,
    })
  }

  return sections
})

const isActive = (url: string): boolean => {
  return route.path === url || route.path.startsWith(url + '/')
}

const isModuleActive = (mod: ModuleSection): boolean => {
  if (mod.directUrl) return isActive(mod.directUrl)
  return mod.items.some((item) => isActive(item.url))
}

const handleModuleClick = (mod: ModuleSection) => {
  if (mod.id === 'copilot') {
    openCopilot()
    activeModule.value = null
    return
  }

  if (mod.directUrl) {
    router.push(mod.directUrl)
    activeModule.value = null
    return
  }

  if (activeModule.value === mod.id) {
    activeModule.value = null
  } else {
    activeModule.value = mod.id
  }
}

const handleItemClick = (url: string) => {
  router.push(url)
  activeModule.value = null
  isMobileMenuOpen.value = false
}

const handleLogout = async () => {
  await logout()
}

const roleLabel = computed(() => {
  if (isSuperAdmin.value) return 'SuperAdmin'
  if (isAdmin.value) return 'Admin'
  if (isOperator.value) return 'Operador'
  if (isClient.value) return 'Cliente'
  if (isMember.value) return 'Miembro'
  return ''
})

const activeModuleData = computed(() => {
  if (!activeModule.value) return null
  return modules.value.find((m) => m.id === activeModule.value) || null
})

// Close drawer on click outside
const handleClickOutside = (e: MouseEvent) => {
  if (!activeModule.value) return
  const target = e.target as HTMLElement
  if (
    drawerRef.value && !drawerRef.value.contains(target) &&
    railRef.value && !railRef.value.contains(target)
  ) {
    activeModule.value = null
  }
}

// Close drawer on route change
const stopRouteWatch = router.afterEach(() => {
  activeModule.value = null
})

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside)
  stopRouteWatch()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("button", {
      class: "fixed top-4 left-4 z-50 flex md:hidden items-center justify-center h-9 w-9 rounded-md border bg-background text-foreground shadow-sm",
      onClick: _cache[0] || (_cache[0] = ($event: any) => (isMobileMenuOpen.value = !isMobileMenuOpen.value))
    }, [
      (!isMobileMenuOpen.value)
        ? (_openBlock(), _createBlock(_unref(Menu), {
            key: 0,
            class: "h-5 w-5"
          }))
        : (_openBlock(), _createBlock(_unref(X), {
            key: 1,
            class: "h-5 w-5"
          }))
    ]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(_Transition, { name: "fade" }, {
        default: _withCtx(() => [
          (isMobileMenuOpen.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "fixed inset-0 z-40 bg-black/50 md:hidden",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (isMobileMenuOpen.value = false))
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ])),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(_Transition, { name: "slide-left" }, {
        default: _withCtx(() => [
          (isMobileMenuOpen.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _cache[4] || (_cache[4] = _createElementVNode("div", { class: "flex aspect-square size-8 items-center justify-center rounded-lg overflow-hidden" }, [
                    _createElementVNode("img", {
                      src: "/img/icons/isotipoblack.svg",
                      alt: "Torcaza",
                      class: "size-8 dark:hidden"
                    }),
                    _createElementVNode("img", {
                      src: "/img/icons/isotipowhite.svg",
                      alt: "Torcaza",
                      class: "size-8 hidden dark:block bg-primary rounded-lg p-0.5"
                    })
                  ], -1)),
                  _createElementVNode("div", _hoisted_3, [
                    _cache[3] || (_cache[3] = _createElementVNode("span", { class: "font-semibold text-sidebar-foreground" }, "Torcaza Projects", -1)),
                    _createElementVNode("span", _hoisted_4, _toDisplayString(roleLabel.value), 1)
                  ])
                ]),
                _createElementVNode("nav", _hoisted_5, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(modules.value, (mod) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: mod.id
                    }, [
                      (mod.directUrl || mod.id === 'copilot')
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            class: _normalizeClass(["flex items-center gap-3 w-full rounded-md px-3 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent transition-colors", { 'bg-sidebar-accent font-medium': mod.directUrl && isActive(mod.directUrl) }]),
                            onClick: ($event: any) => (mod.id === 'copilot' ? _unref(openCopilot)() : handleItemClick(mod.directUrl!))
                          }, [
                            (_openBlock(), _createBlock(_resolveDynamicComponent(mod.icon), { class: "h-5 w-5 shrink-0" })),
                            _createElementVNode("span", null, _toDisplayString(mod.label), 1)
                          ], 10, _hoisted_6))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createElementVNode("div", _hoisted_7, [
                              _createElementVNode("span", _hoisted_8, _toDisplayString(mod.label), 1)
                            ]),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mod.items, (item) => {
                              return (_openBlock(), _createElementBlock("button", {
                                key: item.url,
                                class: _normalizeClass(["flex items-center gap-3 w-full rounded-md px-3 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent transition-colors", { 'bg-sidebar-accent font-medium text-primary': isActive(item.url) }]),
                                onClick: ($event: any) => (handleItemClick(item.url))
                              }, [
                                (_openBlock(), _createBlock(_resolveDynamicComponent(item.icon), { class: "h-4 w-4 shrink-0" })),
                                _createElementVNode("span", null, _toDisplayString(item.title), 1)
                              ], 10, _hoisted_9))
                            }), 128))
                          ], 64))
                    ], 64))
                  }), 128))
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _createVNode(_unref(Avatar), { class: "h-8 w-8" }, {
                      default: _withCtx(() => [
                        (_unref(user)?.avatarUrl)
                          ? (_openBlock(), _createBlock(_unref(AvatarImage), {
                              key: 0,
                              src: _unref(user).avatarUrl
                            }, null, 8, ["src"]))
                          : _createCommentVNode("", true),
                        _createVNode(_unref(AvatarFallback), { class: "text-xs" }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(getInitials)(_unref(user)?.displayName)), 1)
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }),
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("p", _hoisted_13, _toDisplayString(_unref(user)?.displayName), 1),
                      _createElementVNode("p", _hoisted_14, _toDisplayString(_unref(user)?.email), 1)
                    ]),
                    _createElementVNode("button", {
                      onClick: handleLogout,
                      class: "text-muted-foreground hover:text-destructive transition-colors"
                    }, [
                      _createVNode(_unref(LogOut), { class: "h-4 w-4" })
                    ])
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ])),
    _createElementVNode("div", {
      ref_key: "railRef",
      ref: railRef,
      class: "hidden md:flex fixed inset-y-0 left-0 z-30 w-[88px] flex-col bg-sidebar border-r border-sidebar-border"
    }, [
      _createElementVNode("div", _hoisted_15, [
        _createVNode(_unref(RouterLink), {
          to: "/dashboard",
          class: "flex items-center justify-center"
        }, {
          default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
            _createElementVNode("div", { class: "flex aspect-square size-9 items-center justify-center rounded-lg overflow-hidden" }, [
              _createElementVNode("img", {
                src: "/img/icons/isotipoblack.svg",
                alt: "Torcaza",
                class: "size-9 dark:hidden"
              }),
              _createElementVNode("img", {
                src: "/img/icons/isotipowhite.svg",
                alt: "Torcaza",
                class: "size-9 hidden dark:block bg-primary rounded-lg p-0.5"
              })
            ], -1)
          ]))]),
          _: 1
        })
      ]),
      _createElementVNode("nav", _hoisted_16, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(modules.value, (mod) => {
          return (_openBlock(), _createElementBlock("button", {
            key: mod.id,
            class: _normalizeClass(["rail-module group relative flex flex-col items-center justify-center w-full rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150", {
            'rail-module--active !text-primary': activeModule.value === mod.id || (mod.directUrl && isActive(mod.directUrl)) || (!mod.directUrl && mod.id !== 'copilot' && isModuleActive(mod)),
            'rail-module--open !bg-primary/10': activeModule.value === mod.id,
          }]),
            onClick: ($event: any) => (handleModuleClick(mod))
          }, [
            (activeModule.value === mod.id || (mod.directUrl && isActive(mod.directUrl)) || (!mod.directUrl && mod.id !== 'copilot' && isModuleActive(mod)))
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["absolute -left-2 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full bg-primary transition-all duration-200", activeModule.value === mod.id ? 'h-7' : 'h-4'])
                }, null, 2))
              : _createCommentVNode("", true),
            (_openBlock(), _createBlock(_resolveDynamicComponent(mod.icon), {
              class: "h-5 w-5 shrink-0",
              "stroke-width": 1.75
            })),
            _createElementVNode("span", _hoisted_18, _toDisplayString(mod.label), 1)
          ], 10, _hoisted_17))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_19, [
        _createVNode(_unref(RouterLink), {
          to: "/profile",
          class: "block group"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(Avatar), { class: "h-8 w-8 ring-2 ring-transparent group-hover:ring-primary/20 transition-all" }, {
              default: _withCtx(() => [
                (_unref(user)?.avatarUrl)
                  ? (_openBlock(), _createBlock(_unref(AvatarImage), {
                      key: 0,
                      src: _unref(user).avatarUrl
                    }, null, 8, ["src"]))
                  : _createCommentVNode("", true),
                _createVNode(_unref(AvatarFallback), { class: "text-[10px] font-medium" }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(getInitials)(_unref(user)?.displayName)), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ])
    ], 512),
    _createVNode(_Transition, { name: "slide-drawer" }, {
      default: _withCtx(() => [
        (activeModuleData.value && activeModuleData.value.items.length > 0)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              ref_key: "drawerRef",
              ref: drawerRef,
              class: "hidden md:flex fixed inset-y-0 left-[88px] z-20 w-60 flex-col bg-sidebar border-r border-sidebar-border shadow-lg"
            }, [
              _createElementVNode("div", _hoisted_20, [
                _createElementVNode("h2", _hoisted_21, _toDisplayString(activeModuleData.value.label), 1),
                _createElementVNode("button", {
                  class: "text-muted-foreground hover:text-sidebar-foreground transition-colors",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (activeModule.value = null))
                }, [
                  _createVNode(_unref(X), { class: "h-4 w-4" })
                ])
              ]),
              _createElementVNode("nav", _hoisted_22, [
                (activeModuleData.value.groups?.length)
                  ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(activeModuleData.value.groups, (group, gi) => {
                      return (_openBlock(), _createElementBlock(_Fragment, { key: gi }, [
                        (gi > 0)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_23))
                          : _createCommentVNode("", true),
                        (group.label)
                          ? (_openBlock(), _createElementBlock("p", _hoisted_24, _toDisplayString(group.label), 1))
                          : _createCommentVNode("", true),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.items, (item) => {
                          return (_openBlock(), _createElementBlock("button", {
                            key: item.url,
                            class: _normalizeClass(["flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors", { 'bg-primary/10 text-primary font-medium': isActive(item.url) }]),
                            onClick: ($event: any) => (handleItemClick(item.url))
                          }, [
                            (_openBlock(), _createBlock(_resolveDynamicComponent(item.icon), { class: "h-4 w-4 shrink-0" })),
                            _createElementVNode("span", null, _toDisplayString(item.title), 1)
                          ], 10, _hoisted_25))
                        }), 128))
                      ], 64))
                    }), 128))
                  : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(activeModuleData.value.items, (item) => {
                      return (_openBlock(), _createElementBlock("button", {
                        key: item.url,
                        class: _normalizeClass(["flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors", { 'bg-primary/10 text-primary font-medium': isActive(item.url) }]),
                        onClick: ($event: any) => (handleItemClick(item.url))
                      }, [
                        (_openBlock(), _createBlock(_resolveDynamicComponent(item.icon), { class: "h-4 w-4 shrink-0" })),
                        _createElementVNode("span", null, _toDisplayString(item.title), 1)
                      ], 10, _hoisted_26))
                    }), 128))
              ]),
              (_unref(currentTenant)?.name)
                ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                    _createElementVNode("p", _hoisted_28, _toDisplayString(_unref(currentTenant).name), 1)
                  ]))
                : _createCommentVNode("", true)
            ], 512))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})